import * as XLSX from 'xlsx';

// Workflow S5 statement Excel export (Task 12) — the same client-side
// xlsx-building idiom exportPnlExcel reaches via lib/export-pl-xlsx.ts (and
// export-bank-xlsx.ts): aoa_to_sheet → book_new → book_append_sheet →
// writeFile, amounts kept as numbers so Excel can sum them. The P&L breakdown
// export stays on exportPlXlsx itself; this module covers the Balance Sheet
// tab, the Trial Balance tab and the StatementDrillModal voucher rows.

/** Which statement a workbook holds. 'pl' intentionally absent — the P&L
 *  breakdown export reuses exportPlXlsx (multi-sheet, section grouping). */
export type StatementExportTab = 'bs' | 'trial' | 'vouchers';

/** One printable row, shared across the three layouts. */
export interface StatementExportRow {
  /** 'section' renders a standalone section heading (BS Assets/Liabilities/Equity),
   *  'total' a bold total line; default 'data'. */
  kind?: 'section' | 'data' | 'total';
  /** Account "code – name", voucher number, or the total's label. */
  label: string;
  /** BS account balance / voucher amount (single-amount layouts). */
  amount?: number;
  /** Trial-balance columns. */
  opening?: number;
  debit?: number;
  credit?: number;
  ending?: number;
  /** Voucher-level extras. */
  date?: string;
  description?: string;
  invoice?: string;
}

export interface StatementExportMeta {
  /** Header line rendered above the table, e.g. "Balance Sheet as of …". */
  title: string;
  /** Download filename without extension. */
  filename: string;
  /** Sheet name; defaults to a tab-derived name. */
  sheetName?: string;
}

const DEFAULT_SHEET: Record<StatementExportTab, string> = {
  bs: 'Balance Sheet',
  trial: 'Trial Balance',
  vouchers: 'Vouchers',
};

const num = (v: number | undefined) => (typeof v === 'number' && !Number.isNaN(v) ? v : 0);
const round2 = (n: number) => Math.round(n * 100) / 100;

/** Build the sheet body. Column layout follows the on-screen statement
 *  (Bookkeeping BS/TB tabs and the drill modal's voucher table). */
function buildStatementSheet(tab: StatementExportTab, rows: StatementExportRow[], title: string): XLSX.WorkSheet {
  const aoa: (string | number)[][] = [[title], []];

  if (tab === 'trial') {
    aoa.push(['Account', 'Balance b/f', 'Debit', 'Credit', 'Ending']);
    for (const r of rows) {
      aoa.push([
        r.label,
        num(r.opening),
        num(r.debit),
        num(r.credit),
        num(r.ending),
      ]);
    }
    // Trial-balance proof row: Dr and Cr column totals (which must agree).
    // Opening/Ending stay blank — their signs differ by account class, so a
    // column sum would be meaningless.
    aoa.push([
      'Total',
      '',
      round2(rows.reduce((s, r) => s + num(r.debit), 0)),
      round2(rows.reduce((s, r) => s + num(r.credit), 0)),
      '',
    ]);
  } else if (tab === 'vouchers') {
    aoa.push(['Date', 'Voucher No.', 'Description', 'Invoice', 'Amount']);
    for (const r of rows) {
      aoa.push([
        r.date ?? '',
        r.label,
        r.description ?? '',
        r.invoice ?? '',
        num(r.amount),
      ]);
    }
    aoa.push(['Total', '', '', '', round2(rows.reduce((s, r) => s + num(r.amount), 0))]);
  } else {
    // Balance sheet — sections, account rows, section totals.
    aoa.push(['Item', 'Amount']);
    for (const r of rows) {
      if (r.kind === 'section') aoa.push([r.label, '']);
      else aoa.push([r.label, num(r.amount)]);
    }
  }

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = tab === 'trial'
    ? [{ wch: 34 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }]
    : tab === 'vouchers'
      ? [{ wch: 12 }, { wch: 16 }, { wch: 44 }, { wch: 14 }, { wch: 14 }]
      : [{ wch: 40 }, { wch: 16 }];
  return ws;
}

/** Generate and download the .xlsx for one statement tab. */
export function exportStatementExcel(tab: StatementExportTab, rows: StatementExportRow[], meta: StatementExportMeta): void {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, buildStatementSheet(tab, rows, meta.title), meta.sheetName || DEFAULT_SHEET[tab]);
  XLSX.writeFile(wb, `${meta.filename}.xlsx`);
}

// ── Payload mappers (GET /bookkeeping/balance-sheet, /trial-balance) ────────
// Section/total labels mirror Bookkeeping's on-screen BS tab.

interface BsAccount { code: string; name: string; balance: number }
export interface BalanceSheetPayload {
  assets?: BsAccount[];
  liabilities?: BsAccount[];
  equity?: BsAccount[];
  total_assets?: number;
  total_liabilities?: number;
  total_equity?: number;
  as_of?: string;
}

export function bsExportRows(bs: BalanceSheetPayload): StatementExportRow[] {
  const section = (label: string, rows: BsAccount[] | undefined, totalLabel: string, total: number | undefined): StatementExportRow[] => [
    { kind: 'section', label },
    ...(rows || []).map(a => ({ label: `${a.code} – ${a.name}`, amount: a.balance })),
    { kind: 'total', label: totalLabel, amount: total ?? 0 },
  ];
  return [
    ...section('資產 Assets', bs.assets, '總資產 Total Assets', bs.total_assets),
    ...section('負債 Liabilities', bs.liabilities, '總負債 Total Liabilities', bs.total_liabilities),
    ...section('股東權益 Equity', bs.equity, '總權益 Total Equity', bs.total_equity),
  ];
}

export interface TrialBalancePayload {
  data?: { account_code: string; account_name: string; opening_balance?: number; total_debit?: number; total_credit?: number; ending_balance?: number }[];
  as_of?: string;
}

export function trialExportRows(tb: TrialBalancePayload): StatementExportRow[] {
  return (tb.data || []).map(r => ({
    label: `${r.account_code} – ${r.account_name}`,
    opening: r.opening_balance,
    debit: r.total_debit,
    credit: r.total_credit,
    ending: r.ending_balance,
  }));
}

/** Sanitise a tenant/client label for a download filename. */
export function safeFilePart(s: string | null | undefined): string {
  return (s || '').replace(/[^\w.-]+/g, '').slice(0, 40);
}
